import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Container,
  Row,
  Col,
} from "reactstrap";

import { useAuth } from "context/AuthContext";
import { useNavigate } from "react-router-dom";
import constants from "constants";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import { useTranslation } from "context/TranslationProvider";
import FilterCenterFocusTwoToneIcon from "@mui/icons-material/FilterCenterFocusTwoTone";
import DeleteOutlineTwoToneIcon from "@mui/icons-material/DeleteOutlineTwoTone";
import "assets/css/custom.css";

function Profile() {
  const { isAuthenticated, user, updateUser, loading } = useAuth();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [localUserData, setLocalUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [preview, setPreview] = useState(null);
  const [isHover, setIsHover] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/login");
  }, [isAuthenticated, loading, navigate]);

  // Load user data
  useEffect(() => {
    if (user) {
      setLocalUserData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
      });

      const profileImageUrl = user.profilePictureUrl
        ? `${constants.s3Bucket}/profile-pics/${user.profilePictureUrl}?t=${new Date().getTime()}`
        : null;

      setPreview(profileImageUrl);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&_])[A-Za-z\d@$!%*?#&_]{8,}$/;
    return regex.test(password);
  };

  const saveUserChanges = async () => {
    try {
      const res = await userServices.updateUserDetails(user._id, localUserData);
      const dataObj = res?.data?.data || res?.data;
      if (res?.data?.success || dataObj?._id) {
        const updatedUser = { ...user, ...dataObj };
        updateUser(updatedUser);
        notify("success", t(res?.data?.messageKey || "page.users.toast.profileUpdated", res?.data?.message || t("page.users.toast.profileUpdated", "Profile updated successfully.")));
      } else {
        notify("error", t(res?.data?.messageKey || res?.data?.error || "page.users.toast.profileUpdateFailed", res?.data?.message || t("page.users.toast.profileUpdateFailed", "Failed to update profile. Please try again.")));
      }
    } catch {
      notify("error", t("page.users.toast.profileUpdateFailed", "Failed to update profile. Please try again."));
    }
  };

  const updatePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword || !newPassword || !confirmPassword) {
      notify("error", t("page.users.toast.fillAllFields", "Please fill in all required fields."));
      return;
    }

    if (!validatePassword(newPassword)) {
      notify("error", t("page.users.toast.passwordRules", "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character."));
      return;
    }

    if (newPassword !== confirmPassword) {
      notify("error", t("page.users.toast.passwordMismatch", "New password and confirm password do not match."));
      return;
    }

    try {
      const res = await userServices.updatePassword({
        email: user.email,
        currentPassword,
        password: newPassword,
        confirmPassword,
      });

      if (res?.success || res?.message) {
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        notify("success", t(res?.messageKey || "page.users.toast.passwordUpdated", res?.message || t("page.users.toast.passwordUpdated", "Password updated successfully.")));
      } else {
        notify("error", t(res?.messageKey || res?.error || "page.users.toast.passwordUpdateFailed", res?.message || t("page.users.toast.passwordUpdateFailed", "Failed to update password. Please try again.")));
      }
    } catch {
      notify("error", t("page.users.toast.passwordUpdateFailed", "Failed to update password. Please try again."));
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("profilePicture", file);

      const res = await userServices.uploadUserProfilePic(formData);
      const dataObj = res?.data?.data || res?.data?.user || res?.data;

      if (dataObj?.profilePictureUrl) {
        updateUser(dataObj);

        const uploadedUrl = `${constants.s3Bucket}/profile-pics/${dataObj.profilePictureUrl}?t=${new Date().getTime()}`;
        setPreview(uploadedUrl);

        notify("success", t(res?.data?.messageKey || "page.users.toast.profilePictureUpdated", res?.data?.message || t("page.users.toast.profilePictureUpdated", "Profile picture updated successfully.")));
      } else {
        notify("error", t(res?.data?.messageKey || res?.data?.error || "page.users.toast.profilePictureUpdateFailed", res?.data?.message || t("page.users.toast.profilePictureUpdateFailed", "Failed to update profile picture.")));
      }
    } catch (error) {
      notify("error", t("page.users.toast.profilePictureUpdateFailed", "Failed to update profile picture."));
      console.error(error);
    }
  };

  const deleteProfilePicture = async () => {
    if (!user?._id) return;

    try {
      const res = await userServices.deleteUserProfilePic(user._id);
      if (res?.success) {
        updateUser({ ...user, profilePictureUrl: null });
        setPreview(null);
        notify("success", t(res?.messageKey || "page.users.toast.profilePictureDeleted", res?.message || t("page.users.toast.profilePictureDeleted", "Profile picture deleted successfully.")));
      } else {
        notify("error", t(res?.messageKey || res?.error || "page.users.toast.profilePictureDeleteFailed", res?.message || t("page.users.toast.profilePictureDeleteFailed", "Failed to delete profile picture.")));
      }
    } catch {
      notify("error", t("page.users.toast.profilePictureDeleteFailed", "Failed to delete profile picture."));
    }
  };

  if (loading) return null;

  const initials = `${localUserData.firstName?.[0] || ""}${localUserData.lastName?.[0] || ""}`.toUpperCase();

  return (
    <Container className="mt-4" fluid>
      <Row>
        <Col xl="12">
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">{t("page.user-profile.editTitle", "Edit profile")}</h3>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {/* User Info Form */}
              <Form>
                <h6 className="heading-small text-muted mb-4">{t("page.user-profile.sections.userInfo", "User information")}</h6>
                <div className="pl-lg-4">
                  <Row className="align-items-center">
                    {/* Profile Picture */}
                    <Col lg="2" className="d-flex justify-content-center">
                      <div
                        className="profile-picture-wrapper"
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                      >
                        {preview ? (
                          <img src={preview} alt="Profile" className="profile-picture" />
                        ) : (
                          <div className="profile-picture-initials">{initials || "U"}</div>
                        )}

                        {isHover && (
                          <div className="profile-picture-overlay">
                            <label htmlFor="profilePicInput">
                              <FilterCenterFocusTwoToneIcon className="icon mt-2" />
                            </label>
                            {preview && <DeleteOutlineTwoToneIcon className="icon" onClick={deleteProfilePicture} />}
                          </div>
                        )}

                        <input
                          type="file"
                          id="profilePicInput"
                          accept="image/*"
                          className="d-none"
                          onChange={handleProfilePictureChange}
                        />
                      </div>
                    </Col>

                    {/* User Info */}
                    <Col lg="10">
                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label className="form-control-label" htmlFor="input-first-name">
                              {t("auth.signup.firstName", "First Name")}
                            </label>
                            <Input
                              name="firstName"
                              value={localUserData.firstName || ""}
                              onChange={handleChange}
                              id="input-first-name"
                              placeholder={t("auth.signup.firstName", "First Name")}
                              type="text"
                            />
                          </FormGroup>
                        </Col>

                        <Col lg="6">
                          <FormGroup>
                            <label className="form-control-label" htmlFor="input-last-name">
                              {t("auth.signup.lastName", "Last Name")}
                            </label>
                            <Input
                              name="lastName"
                              value={localUserData.lastName || ""}
                              onChange={handleChange}
                              id="input-last-name"
                              placeholder={t("auth.signup.lastName", "Last Name")}
                              type="text"
                            />
                          </FormGroup>
                        </Col>

                        <Col lg="12">
                          <FormGroup>
                            <label className="form-control-label" htmlFor="input-email">
                              {t("auth.login.email", "Email")}
                            </label>
                            <Input
                              name="email"
                              value={localUserData.email || ""}
                              onChange={handleChange}
                              id="input-email"
                              placeholder={t("auth.login.email", "Email")}
                              type="email"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end">
                    <Button color="warning" onClick={saveUserChanges} size="md">
                      {t("button.save", "Save")}
                    </Button>
                  </div>
                </div>

                <hr className="my-4" />

                {/* Change Password */}
                <h6 className="heading-small text-muted mb-4">{t("page.user-profile.sections.changePassword", "Change Your Password")}</h6>
                <div className="pl-lg-4">
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <label className="form-control-label" htmlFor="input-Cpassword">
                          {t("page.user-profile.form.currentPassword", "Current Password")}
                        </label>
                        <InputGroup className="input-group-merge">
                          <Input
                            id="input-Cpassword"
                            placeholder={t("page.user-profile.form.currentPassword", "Current Password")}
                            type={showCurrentPassword ? "text" : "password"}
                            name="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                          />
                          <InputGroupAddon addonType="append">
                            <InputGroupText
                              className="password-toggle cursor-pointer"
                              onClick={() => setShowCurrentPassword((prev) => !prev)}
                            >
                              <i
                                className={showCurrentPassword ? "fa fa-eye-slash" : "fa fa-eye"}
                              />
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormGroup>
                    </Col>
 
                    <Col lg="6">
                      <FormGroup>
                        <label className="form-control-label" htmlFor="input-Npassword">
                          {t("auth.reset-password.newPassword", "New Password")}
                        </label>
                        <InputGroup className="input-group-merge">
                          <Input
                            id="input-Npassword"
                            placeholder={t("auth.reset-password.newPassword", "New Password")}
                            type={showNewPassword ? "text" : "password"}
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                          />
                          <InputGroupAddon addonType="append">
                            <InputGroupText
                              className="password-toggle cursor-pointer"
                              onClick={() => setShowNewPassword((prev) => !prev)}
                            >
                              <i
                                className={showNewPassword ? "fa fa-eye-slash" : "fa fa-eye"}
                              />
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormGroup>
                    </Col>
 
                    <Col lg="6">
                      <FormGroup>
                        <label className="form-control-label" htmlFor="input-Cirpassword">
                          {t("auth.reset-password.confirmPassword", "Confirm New Password")}
                        </label>
                        <InputGroup className="input-group-merge">
                          <Input
                            id="input-Cirpassword"
                            placeholder={t("auth.reset-password.confirmPassword", "Confirm New Password")}
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                          />
                          <InputGroupAddon addonType="append">
                            <InputGroupText
                              className="password-toggle cursor-pointer"
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                            >
                              <i
                                className={showConfirmPassword ? "fa fa-eye-slash" : "fa fa-eye"}
                              />
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end">
                    <Button color="warning" onClick={updatePassword} size="md">
                      {t("auth.reset-password.submitButton", "Reset Password")}
                    </Button>
                  </div>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Profile;